import type {
  BlueprintRunActivateDecisionRequestDto,
  BlueprintRunCandidateSetDto,
  BlueprintRunCreateCandidateSetRequestDto,
  BlueprintRunCreateDecisionPackRequestDto,
  BlueprintRunCreateRequestDto,
  BlueprintRunCreateSupervisionRecordRequestDto,
  BlueprintRunDecisionPackDto,
  BlueprintRunIngestVerificationActualsArtifactRequestDto,
  BlueprintRunLedgerSubmissionDto,
  BlueprintRunDto,
  BlueprintRunEvidenceLedgerDto,
  BlueprintRunProcessMapDto,
  BlueprintRunRootCauseClusterSetDto,
  BlueprintRunRuntimeRerunRequestDto,
  BlueprintRunRuntimeStartRequestDto,
  BlueprintRunRuntimeStatusDto,
  BlueprintRunLaunchPreflightRequestDto,
  BlueprintRunLaunchPreflightResponseDto,
  BlueprintRunPackManifestDto,
  BlueprintProgramDetailDto,
  BlueprintProgramProjectionDto,
  BlueprintRunPortfolioSequenceRequestDto,
  BlueprintRunPortfolioSequenceResultDto,
  BlueprintRunStrategicObjectiveListItemDto,
  BlueprintRunSupervisionRecordDto,
  BlueprintRunSupervisionRecordMutationDto,
  BlueprintRunSynthesisSnapshotDto,
  BlueprintRunVerificationPlanDto,
} from '@helix/lib';
import { apiFetch } from './apiFetch';

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

const blueprintRunSynthesisRouteByPackId: Record<string, string> = {
  'value-creation-blueprint': 'vcb-synthesis',
  'operating-model-blueprint': 'operating-model-synthesis',
  'transformation-blueprint': 'transformation-synthesis',
  'cost-transformation-blueprint': 'cost-transformation-synthesis',
  'integration-blueprint': 'integration-synthesis',
  'growth-blueprint': 'growth-synthesis',
  'turnaround-blueprint': 'turnaround-synthesis',
};

function synthesisRouteSegmentForPackId(packId: string): string {
  const normalized = String(packId ?? '').trim();
  if (!normalized) throw new Error('Missing packId');
  const segment = blueprintRunSynthesisRouteByPackId[normalized];
  if (!segment) throw new Error(`Blueprint synthesis is not available for pack ${normalized}`);
  return segment;
}

export async function listBlueprintRunPacks(): Promise<BlueprintRunPackManifestDto[]> {
  const res = await apiFetch('/v1/blueprint-runs/packs', { cache: 'no-cache' });
  return handle<BlueprintRunPackManifestDto[]>(res);
}

export async function listBlueprintPrograms(): Promise<BlueprintProgramProjectionDto[]> {
  const res = await apiFetch('/v1/blueprint-runs/programs', { cache: 'no-cache' });
  return handle<BlueprintProgramProjectionDto[]>(res);
}

export async function getBlueprintProgram(programSlug: string): Promise<BlueprintProgramDetailDto> {
  const normalized = String(programSlug ?? '').trim();
  if (!normalized) throw new Error('Missing programSlug');
  const res = await apiFetch(`/v1/blueprint-runs/programs/${encodeURIComponent(normalized)}`, {
    cache: 'no-cache',
  });
  return handle<BlueprintProgramDetailDto>(res);
}

export async function launchBlueprintRunPreflight(
  payload: BlueprintRunLaunchPreflightRequestDto,
): Promise<BlueprintRunLaunchPreflightResponseDto> {
  const res = await apiFetch('/v1/blueprint-runs/launch-preflight', {
    method: 'POST',
    json: payload,
    cache: 'no-cache',
  });
  return handle<BlueprintRunLaunchPreflightResponseDto>(res);
}

export async function createBlueprintRun(payload: BlueprintRunCreateRequestDto): Promise<BlueprintRunDto> {
  const res = await apiFetch('/v1/blueprint-runs/runs', {
    method: 'POST',
    json: payload,
    cache: 'no-cache',
  });
  return handle<BlueprintRunDto>(res);
}

export async function createOperatingModelBlueprintFromVcbRun(runId: string): Promise<BlueprintRunDto> {
  const normalized = String(runId ?? '').trim();
  if (!normalized) throw new Error('Missing runId');
  const res = await apiFetch(
    `/v1/blueprint-runs/runs/${encodeURIComponent(normalized)}/operating-model-blueprint`,
    {
      method: 'POST',
      cache: 'no-cache',
    },
  );
  return handle<BlueprintRunDto>(res);
}

export async function listBlueprintRunsForStrategy(strategyId: string): Promise<BlueprintRunDto[]> {
  const normalized = String(strategyId ?? '').trim();
  if (!normalized) throw new Error('Missing strategyId');
  const res = await apiFetch(`/v1/blueprint-runs/strategies/${encodeURIComponent(normalized)}/runs`, {
    cache: 'no-cache',
  });
  return handle<BlueprintRunDto[]>(res);
}

export async function listBlueprintRunStrategicObjectives(
  strategyId: string,
): Promise<BlueprintRunStrategicObjectiveListItemDto[]> {
  const normalized = String(strategyId ?? '').trim();
  if (!normalized) throw new Error('Missing strategyId');
  const res = await apiFetch(`/v1/blueprint-runs/strategies/${encodeURIComponent(normalized)}/strategic-objectives`, {
    cache: 'no-cache',
  });
  return handle<BlueprintRunStrategicObjectiveListItemDto[]>(res);
}

export async function getBlueprintRun(runId: string): Promise<BlueprintRunDto> {
  const normalized = String(runId ?? '').trim();
  if (!normalized) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalized)}`, {
    cache: 'no-cache',
  });
  return handle<BlueprintRunDto>(res);
}

export async function getBlueprintRunVcbSynthesis(runId: string): Promise<BlueprintRunSynthesisSnapshotDto> {
  return getBlueprintRunSynthesisForPack(runId, 'value-creation-blueprint');
}

export async function getBlueprintRunSynthesisForPack(
  runId: string,
  packId: string,
): Promise<BlueprintRunSynthesisSnapshotDto> {
  const normalized = String(runId ?? '').trim();
  if (!normalized) throw new Error('Missing runId');
  const segment = synthesisRouteSegmentForPackId(packId);
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalized)}/${segment}`, {
    cache: 'no-cache',
  });
  return handle<BlueprintRunSynthesisSnapshotDto>(res);
}

export async function recomputeBlueprintRunVcbSynthesis(runId: string): Promise<BlueprintRunSynthesisSnapshotDto> {
  return recomputeBlueprintRunSynthesisForPack(runId, 'value-creation-blueprint');
}

export async function recomputeBlueprintRunSynthesisForPack(
  runId: string,
  packId: string,
): Promise<BlueprintRunSynthesisSnapshotDto> {
  const normalized = String(runId ?? '').trim();
  if (!normalized) throw new Error('Missing runId');
  const segment = synthesisRouteSegmentForPackId(packId);
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalized)}/${segment}/recompute`, {
    method: 'POST',
    cache: 'no-cache',
  });
  return handle<BlueprintRunSynthesisSnapshotDto>(res);
}

export async function listBlueprintRunVcbSupervisionRecords(
  runId: string,
): Promise<BlueprintRunSupervisionRecordDto[]> {
  const normalized = String(runId ?? '').trim();
  if (!normalized) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalized)}/vcb-supervision-records`, {
    cache: 'no-cache',
  });
  return handle<BlueprintRunSupervisionRecordDto[]>(res);
}

export async function createBlueprintRunVcbSupervisionRecord(
  runId: string,
  payload: BlueprintRunCreateSupervisionRecordRequestDto,
): Promise<BlueprintRunSupervisionRecordMutationDto> {
  const normalized = String(runId ?? '').trim();
  if (!normalized) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalized)}/vcb-supervision-records`, {
    method: 'POST',
    json: payload,
    cache: 'no-cache',
  });
  return handle<BlueprintRunSupervisionRecordMutationDto>(res);
}

export async function getBlueprintRunRuntimeStatus(runId: string): Promise<BlueprintRunRuntimeStatusDto> {
  const normalized = String(runId ?? '').trim();
  if (!normalized) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalized)}/runtime`, {
    cache: 'no-cache',
  });
  return handle<BlueprintRunRuntimeStatusDto>(res);
}

export async function getBlueprintRunEvidenceLedger(runId: string): Promise<BlueprintRunEvidenceLedgerDto> {
  const normalized = String(runId ?? '').trim();
  if (!normalized) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalized)}/evidence`, {
    cache: 'no-cache',
  });
  return handle<BlueprintRunEvidenceLedgerDto>(res);
}

export async function getBlueprintRunProcessMap(runId: string): Promise<BlueprintRunProcessMapDto> {
  const normalized = String(runId ?? '').trim();
  if (!normalized) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalized)}/process-map`, {
    cache: 'no-cache',
  });
  return handle<BlueprintRunProcessMapDto>(res);
}

export async function getBlueprintRunRootCauseClusterSet(runId: string): Promise<BlueprintRunRootCauseClusterSetDto> {
  const normalized = String(runId ?? '').trim();
  if (!normalized) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalized)}/root-causes`, {
    cache: 'no-cache',
  });
  return handle<BlueprintRunRootCauseClusterSetDto>(res);
}

export async function startBlueprintRunRuntime(params: {
  runId: string;
  payload: BlueprintRunRuntimeStartRequestDto;
}): Promise<BlueprintRunRuntimeStatusDto> {
  const normalizedRunId = String(params.runId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/runtime/start`, {
    method: 'POST',
    json: params.payload,
    cache: 'no-cache',
  });
  return handle<BlueprintRunRuntimeStatusDto>(res);
}

export async function rerunBlueprintRunRuntime(params: {
  runId: string;
  payload: BlueprintRunRuntimeRerunRequestDto;
}): Promise<BlueprintRunRuntimeStatusDto> {
  const normalizedRunId = String(params.runId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/runtime/rerun`, {
    method: 'POST',
    json: params.payload,
    cache: 'no-cache',
  });
  return handle<BlueprintRunRuntimeStatusDto>(res);
}

export async function listBlueprintRunCandidateSets(runId: string): Promise<BlueprintRunCandidateSetDto[]> {
  const normalizedRunId = String(runId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/candidate-sets`, {
    cache: 'no-cache',
  });
  return handle<BlueprintRunCandidateSetDto[]>(res);
}

export async function getBlueprintRunCandidateSet(
  runId: string,
  candidateSetId: string,
): Promise<BlueprintRunCandidateSetDto> {
  const normalizedRunId = String(runId ?? '').trim();
  const normalizedCandidateSetId = String(candidateSetId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  if (!normalizedCandidateSetId) throw new Error('Missing candidateSetId');
  const res = await apiFetch(
    `/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/candidate-sets/${encodeURIComponent(normalizedCandidateSetId)}`,
    { cache: 'no-cache' },
  );
  return handle<BlueprintRunCandidateSetDto>(res);
}

export async function createBlueprintRunCandidateSet(
  runId: string,
  payload: BlueprintRunCreateCandidateSetRequestDto,
): Promise<BlueprintRunCandidateSetDto> {
  const normalizedRunId = String(runId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/candidate-sets`, {
    method: 'POST',
    json: payload,
    cache: 'no-cache',
  });
  return handle<BlueprintRunCandidateSetDto>(res);
}

export async function sequenceBlueprintRunCandidateSet(params: {
  runId: string;
  candidateSetId: string;
  payload: BlueprintRunPortfolioSequenceRequestDto;
}): Promise<BlueprintRunPortfolioSequenceResultDto> {
  const normalizedRunId = String(params.runId ?? '').trim();
  const normalizedCandidateSetId = String(params.candidateSetId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  if (!normalizedCandidateSetId) throw new Error('Missing candidateSetId');
  const res = await apiFetch(
    `/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/candidate-sets/${encodeURIComponent(normalizedCandidateSetId)}/sequence`,
    {
      method: 'POST',
      json: params.payload,
      cache: 'no-cache',
    },
  );
  return handle<BlueprintRunPortfolioSequenceResultDto>(res);
}

export async function listBlueprintRunPortfolioHandoffs(runId: string): Promise<BlueprintRunPortfolioSequenceResultDto[]> {
  const normalizedRunId = String(runId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/portfolio-handoffs`, {
    cache: 'no-cache',
  });
  return handle<BlueprintRunPortfolioSequenceResultDto[]>(res);
}

export async function getBlueprintRunPortfolioHandoff(
  runId: string,
  handoffId: string,
): Promise<BlueprintRunPortfolioSequenceResultDto> {
  const normalizedRunId = String(runId ?? '').trim();
  const normalizedHandoffId = String(handoffId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  if (!normalizedHandoffId) throw new Error('Missing handoffId');
  const res = await apiFetch(
    `/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/portfolio-handoffs/${encodeURIComponent(normalizedHandoffId)}`,
    { cache: 'no-cache' },
  );
  return handle<BlueprintRunPortfolioSequenceResultDto>(res);
}

export async function createBlueprintRunDecisionPack(
  runId: string,
  payload: BlueprintRunCreateDecisionPackRequestDto,
): Promise<BlueprintRunDecisionPackDto> {
  const normalizedRunId = String(runId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/decision-packs`, {
    method: 'POST',
    json: payload,
    cache: 'no-cache',
  });
  return handle<BlueprintRunDecisionPackDto>(res);
}

export async function listBlueprintRunDecisionPacks(runId: string): Promise<BlueprintRunDecisionPackDto[]> {
  const normalizedRunId = String(runId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/decision-packs`, {
    cache: 'no-cache',
  });
  return handle<BlueprintRunDecisionPackDto[]>(res);
}

export async function getBlueprintRunDecisionPack(
  runId: string,
  decisionPackId: string,
): Promise<BlueprintRunDecisionPackDto> {
  const normalizedRunId = String(runId ?? '').trim();
  const normalizedDecisionPackId = String(decisionPackId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  if (!normalizedDecisionPackId) throw new Error('Missing decisionPackId');
  const res = await apiFetch(
    `/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/decision-packs/${encodeURIComponent(normalizedDecisionPackId)}`,
    { cache: 'no-cache' },
  );
  return handle<BlueprintRunDecisionPackDto>(res);
}

export async function submitBlueprintRunDecisionPack(
  runId: string,
  decisionPackId: string,
): Promise<BlueprintRunLedgerSubmissionDto> {
  const normalizedRunId = String(runId ?? '').trim();
  const normalizedDecisionPackId = String(decisionPackId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  if (!normalizedDecisionPackId) throw new Error('Missing decisionPackId');
  const res = await apiFetch(
    `/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/decision-packs/${encodeURIComponent(normalizedDecisionPackId)}/submit`,
    {
      method: 'POST',
      cache: 'no-cache',
    },
  );
  return handle<BlueprintRunLedgerSubmissionDto>(res);
}

export async function approveBlueprintRunDecisionPack(
  runId: string,
  decisionPackId: string,
): Promise<BlueprintRunLedgerSubmissionDto> {
  const normalizedRunId = String(runId ?? '').trim();
  const normalizedDecisionPackId = String(decisionPackId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  if (!normalizedDecisionPackId) throw new Error('Missing decisionPackId');
  const res = await apiFetch(
    `/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/decision-packs/${encodeURIComponent(normalizedDecisionPackId)}/approve`,
    {
      method: 'POST',
      cache: 'no-cache',
    },
  );
  return handle<BlueprintRunLedgerSubmissionDto>(res);
}

export async function activateBlueprintRunDecisionPack(params: {
  runId: string;
  decisionPackId: string;
  payload: BlueprintRunActivateDecisionRequestDto;
}): Promise<BlueprintRunVerificationPlanDto> {
  const normalizedRunId = String(params.runId ?? '').trim();
  const normalizedDecisionPackId = String(params.decisionPackId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  if (!normalizedDecisionPackId) throw new Error('Missing decisionPackId');
  const res = await apiFetch(
    `/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/decision-packs/${encodeURIComponent(normalizedDecisionPackId)}/activate`,
    {
      method: 'POST',
      json: params.payload,
      cache: 'no-cache',
    },
  );
  return handle<BlueprintRunVerificationPlanDto>(res);
}

export async function ingestBlueprintRunVerificationActualsArtifact(params: {
  runId: string;
  decisionPackId: string;
  payload: BlueprintRunIngestVerificationActualsArtifactRequestDto;
}): Promise<BlueprintRunVerificationPlanDto> {
  const normalizedRunId = String(params.runId ?? '').trim();
  const normalizedDecisionPackId = String(params.decisionPackId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  if (!normalizedDecisionPackId) throw new Error('Missing decisionPackId');
  const res = await apiFetch(
    `/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/decision-packs/${encodeURIComponent(normalizedDecisionPackId)}/verification-actuals-artifacts`,
    {
      method: 'POST',
      json: params.payload,
      cache: 'no-cache',
    },
  );
  return handle<BlueprintRunVerificationPlanDto>(res);
}

export async function getBlueprintRunVerificationPlan(
  runId: string,
  decisionPackId: string,
): Promise<BlueprintRunVerificationPlanDto> {
  const normalizedRunId = String(runId ?? '').trim();
  const normalizedDecisionPackId = String(decisionPackId ?? '').trim();
  if (!normalizedRunId) throw new Error('Missing runId');
  if (!normalizedDecisionPackId) throw new Error('Missing decisionPackId');
  const res = await apiFetch(
    `/v1/blueprint-runs/runs/${encodeURIComponent(normalizedRunId)}/decision-packs/${encodeURIComponent(normalizedDecisionPackId)}/verification-plan`,
    { cache: 'no-cache' },
  );
  return handle<BlueprintRunVerificationPlanDto>(res);
}
